import { Inventory } from "./types";
import { InventorySelectors } from "./selectors";
import fetchWrapper from "src/utils/fetchWrapper";
import { ToasterActions } from "src/redux/Toaster/actions";
import { AuthSelectors } from "src/redux/Auth/selectors";
import { FETCH_STATES } from "src/redux/Tx/constants";
import { MD } from "src/redux/MarketData/types";
import { ERRORS, UPLOAD_INVENTORY_MODES } from "src/redux/Inventory/constants";
import { setDateFormat } from "src/utils";
import { getListSettingData } from "src/utils/converting";
import Math from "src/utils/math";
import { SpinnerTypes } from "src/redux/Spinner/types";
import { SpinnerActions } from "src/redux/Spinner/actions";
import { WebWorkerActions } from "../WebWorker/actions";
import { TopBarSelectors } from "src/redux/TopBar/selectors";

export const InventoryActions = {
    SetUploadEntries: (entries) => {
        return (dispatch) => {
            dispatch({
                type: Inventory.SET_UPLOAD_ENTRIES,
                entries: entries.map((row) =>
                    getListSettingData(
                        {
                            ...row,
                            Maturity: new Date("9999-12-31"),
                            marketData: {
                                status: FETCH_STATES.FETCHING,
                            },
                        },
                        row.ListName
                    )
                ),
            });
            return dispatch(InventoryActions.GetMarketDataBatch(entries));
        };
    },

    SetUploadMode: (mode) => ({
        type: Inventory.SET_UPLOAD_MODE,
        mode,
    }),

    DeleteUploadRow: (rowId) => ({
        type: Inventory.DELETE_UPLOAD_ROW,
        rowId,
    }),

    UpdateUploadRow: (rows) => {
        return (dispatch) => {
            dispatch({
                type: Inventory.UPDATE_UPLOAD_ROW,
                rows,
            });
            dispatch({
                type: Inventory.MARK_REPLACING_UPLOAD_ROW,
            });
        };
    },

    StartRowEdit: (rowId) => {
        return (dispatch) => {
            dispatch({
                type: Inventory.START_ROW_EDIT,
                rowId,
            });
        };
    },

    CancelRowEdit: (rowId) => {
        return (dispatch) => {
            dispatch({
                type: Inventory.CANCEL_ROW_EDIT,
                rowId,
            });
        };
    },

    ApplyRowEdit: (rowId) => {
        return (dispatch) => {
            dispatch({
                type: Inventory.APPLY_ROW_EDIT,
                rowId,
            });
        };
    },

    SetEditedField: (rowId, field, value) => {
        return (dispatch) => {
            dispatch({
                type: Inventory.SET_EDITED_FIELD,
                rowId,
                field,
                value,
            });
        };
    },

    UploadInventory: () => {
        return async (dispatch, getState) => {
            const state = getState();
            dispatch(
                SpinnerActions.Open({ spinnerType: SpinnerTypes.INVENTORY })
            );

            if (!InventorySelectors.IsUploadDataValid(state)) {
                dispatch({
                    type: Inventory.ERROR,
                    error: {
                        type: ERRORS.UPLOAD_TABLE,
                        message: "Found rows with errors",
                    },
                });

                dispatch(
                    SpinnerActions.Close({
                        spinnerType: SpinnerTypes.INVENTORY,
                    })
                );
                return Promise.resolve();
            }

            const mappedInventory = InventorySelectors.GetUploadRows(state).map(
                (e) => {
                    return {
                        uploadedQuantity: e.MyQuantity,
                        preferredRate: Math.round(e.CalculatedRate, 2),
                        security: e.marketData.data,
                        listName: e.ListName,
                        callable: e.Callable,
                        dividend: e.Dividend,
                        txType: e.TxType,
                        intermediary: e.Intermediary,
                        fr: e.FR,
                        valueDate: setDateFormat(e.ValueDate, "-"),
                        maturityDate:
                            setDateFormat(e.Maturity, "-") || "9999-12-31",
                    };
                }
            );

            const request = { entries: mappedInventory };
            const uploadMode = InventorySelectors.GetUploadMode(state);
            const url =
                uploadMode === UPLOAD_INVENTORY_MODES.ADD
                    ? AuthSelectors.BuildInventoryAdd(state)
                    : AuthSelectors.BuildInventoryReplace(state);

            return dispatch(
                WebWorkerActions.uploadInventory({
                    cmd: "start",
                    requestBody: request,
                    apiUrl: url,
                })
            );
        };
    },

    UpdateInventoryEntries: (rowId) => {
        return async (dispatch, getState) => {
            const state = getState();
            dispatch(
                SpinnerActions.Open({ spinnerType: SpinnerTypes.INVENTORY })
            );

            const mappedInventory = InventorySelectors.GetInventoryList(state)
                .filter((e) => `${e.ListName}-${e.ISIN}` === rowId)
                .map((e) => {
                    return {
                        uploadedQuantity: e.UploadedQuantity,
                        preferredRate: e.PreferredRate,
                        security: { id: e.SecurityId },
                        listName: e.ListName,
                        callable: e.Callable,
                        dividend: e.Dividend,
                        txType: e.TxType,
                        intermediary: e.Intermediary,
                        fr: e.FR,
                        valueDate: setDateFormat(e.ValueDate, "-"),
                        maturityDate:
                            setDateFormat(e.Maturity, "-") || "9999-12-31",
                    };
                });

            const request = { entries: mappedInventory };
            const url = AuthSelectors.BuildInventoryUpdate(state);

            return dispatch(
                WebWorkerActions.updateInventory({
                    cmd: "start",
                    requestBody: request,
                    apiUrl: url,
                })
            );
        };
    },

    DeleteInventoryEntries: (inventoryDomainIds) => {
        return (dispatch, getState) => {
            const state = getState();

            dispatch(
                SpinnerActions.Open({ spinnerType: SpinnerTypes.INVENTORY })
            );

            const url = AuthSelectors.BuildDeleteInventoryEntries(state);

            return dispatch(
                fetchWrapper(url, {
                    method: "DELETE",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ inventoryIds: inventoryDomainIds }),
                })
            )
                .then(() => {
                    // Success case: Close spinner
                    dispatch(
                        SpinnerActions.Close({
                            spinnerType: SpinnerTypes.INVENTORY,
                        })
                    );
                })
                .catch((error) => {
                    // Error case: Close spinner first, then show error message
                    dispatch(
                        SpinnerActions.Close({
                            spinnerType: SpinnerTypes.INVENTORY,
                        })
                    );

                    dispatch({
                        type: Inventory.ERROR,
                        error: {
                            type: ERRORS.CLEAR,
                            message: error.message,
                        },
                    });
                    dispatch(
                        ToasterActions.NewError("Error deleting inventory")
                    );
                });
        };
    },

    ClearInventory: () => {
        return (dispatch, getState) => {
            const state = getState();

            dispatch(
                SpinnerActions.Open({ spinnerType: SpinnerTypes.INVENTORY })
            );

            const url = AuthSelectors.BuildInventoryDelete(state);

            return dispatch(
                fetchWrapper(url, {
                    method: "DELETE",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    },
                })
            ).catch((error) => {
                dispatch(
                    SpinnerActions.Close({
                        spinnerType: SpinnerTypes.INVENTORY,
                    })
                );
                dispatch({
                    type: Inventory.ERROR,
                    error: {
                        type: ERRORS.CLEAR,
                        message: error.message,
                    },
                });
                dispatch(ToasterActions.NewError("Error clearing inventory"));
            });
        };
    },

    GetSpecificInventory: (security) => {
        return (dispatch, getState) => {
            const url = AuthSelectors.BuildSpecificInventory(
                getState(),
                security
            );
            return dispatch(fetchWrapper(url))
                .then((result) => {
                    if (result) {
                        return dispatch({
                            type: Inventory.NEW_INVENTORY,
                            inventory: result,
                        });
                    }
                })
                .catch((error) => {
                    dispatch({
                        type: Inventory.ERROR,
                        error: {
                            type: ERRORS.GET_ONE_INVENTORY,
                            message: error.message,
                        },
                    });
                    // dispatch(ToasterActions.NewError("Error fetching inventory for security")) Backend gives 404 and empty response on not found, which popped this up. Just disabling for now
                });
        };
    },
    GetTodayInventory: () => {
        return (dispatch, getState) => {
            const url = AuthSelectors.BuildTodayInventory(getState());
            return dispatch(fetchWrapper(url))
                .then((result) => {
                    if (result) {
                        //There may be none
                        return dispatch({
                            type: Inventory.NEW_INVENTORY_LIST,
                            inventory: result,
                        });
                    }
                })
                .catch((error) => {
                    dispatch({
                        type: Inventory.ERROR,
                        error: {
                            type: ERRORS.GET_TODAY_INVENTORY,
                            message: error.message,
                        },
                    });
                    // dispatch(ToasterActions.NewError("Error fetching inventory for security")) Backend gives 404 and empty response on not found, which popped this up. Just disabling for now
                });
        };
    }, // TODO: Use MD action instead
    GetMarketDataBatch: (rowData) => {
        // get ticker information in the list
        return async (dispatch, getState) => {
            const listSecurityId = rowData.map((row) => row.SecurityId);
            const role = TopBarSelectors.Role(getState());

            // get data from storage
            return dispatch(
                WebWorkerActions.getStockData({
                    cmd: "start",
                    listSecurityId: listSecurityId,
                    role: role,
                    apiUrl: AuthSelectors.MarketDataBatchUrl(getState()),
                    isInventoryStock: true,
                })
            );
        };
    },

    GetMarketData: (stockSymbol) => {
        // update one ticker
        return (dispatch, getState) => {
            return dispatch(
                fetchWrapper(
                    AuthSelectors.BuildMarketDataUrl(getState(), stockSymbol)
                )
            )
                .then((result) => {
                    dispatch({
                        type: MD.MARKET_DATA_UPDATE,
                        result,
                        stockSymbol,
                    });
                })
                .catch((error) => {
                    dispatch({
                        type: MD.MD_ERROR,
                        stockSymbol,
                        error: error.message,
                    });
                });
        };
    },

    ApplyBenchmarkFee: (multiplier) => {
        return (dispatch) => {
            dispatch({
                type: Inventory.APPLY_REF_RATE,
                multiplier,
            });
        };
    },

    SetSelectedInventory: (rows) => ({
        type: Inventory.SET_SELECTED_INVENTORY,
        rows,
    }),

    ApplyMaturity: (maturity) => ({
        type: Inventory.APPLY_MATURITY,
        maturity,
    }),

    ApplyIntermediary: (intermediary) => ({
        type: Inventory.APPLY_INTERMEDIARY,
        intermediary,
    }),

    //table expand
    ExpandTable: () => {
        return (dispatch) => {
            dispatch({
                type: Inventory.EXPAND_TABLE,
            });
        };
    },

    CollapseTable: () => {
        return (dispatch) => {
            dispatch({
                type: Inventory.COLLAPSE_TABLE,
            });
        };
    },
};
